/**
 * Library entries that travel INSIDE a project's sync document. PURE - no
 * signals, no storage, no network.
 *
 * WHY THIS EXISTS (2026-09-28). A sequencer item carries only `sequencerId`
 * and a macro item only `macroId`; the pattern and the lanes live in this
 * browser's own library (`opbuttons-sequencers-v1`, `opbuttons-macros-v1`).
 * Project sync carried the item and not what it points at, so a sequencer
 * built on the laptop arrived on the iPad EMPTY. Found building the RIG TEST
 * page; the export bundle always carried them, sync never did.
 *
 * WHY INSIDE THE PROJECT DOC and not a `sequencers` doc of its own: OPBridge
 * replicates only `project-*` docs across the mesh (`default_doc_sync_targets`
 * in its `doc_sync.rs`). A separate doc would reach an iPad on the SAME bridge
 * and never one whose primary is the other machine - which is exactly the
 * rig, where the Helix needs the Mac as the iPad's primary.
 *
 * Each project doc carries the definitions ITS items reference. The unit of
 * merge is the ENTRY (one sequencer, one macro), decided by the entry's own
 * `modifiedAt`, independent of which project doc brought it and of which
 * side won the project merge. A sequencer shared by two projects can arrive
 * twice; the newer copy wins.
 *
 * TIES GO TO THE INCOMING COPY, for convergence: two devices each holding a
 * different copy with the same (or no) `modifiedAt` would otherwise each keep
 * their own and re-push it forever. Same reasoning as `projectMerge.ts`.
 *
 * NO TOMBSTONES, deliberately. An entry travels only while some project item
 * references it, so removing the item stops it travelling. A library entry
 * deleted on one device while another project still references it comes back,
 * which is right: that project still uses it.
 */

import type { Project } from './projectStore.ts';

export interface LibraryEntry {
  id: string;
  modifiedAt?: number;
}

function withoutStamp(e: LibraryEntry): string {
  const { modifiedAt: _m, ...rest } = e;
  return JSON.stringify(rest);
}

/**
 * Stamp `modifiedAt = now` on every entry of `next` that is new or whose
 * content differs from its `prev` copy. Unchanged entries keep their stamp.
 *
 * Runs on EVERY local write, undo and redo included. An undo that restored an
 * old stamp would make the restored version look OLDER than the copy already
 * on the bridge, and the next sync would silently redo the edit.
 */
export function stampChanged<T extends LibraryEntry>(prev: readonly T[], next: readonly T[], now: number): T[] {
  const prevById = new Map(prev.map((e) => [e.id, e]));
  return next.map((e) => {
    const p = prevById.get(e.id);
    if (p === e) return e;
    if (p && withoutStamp(p) === withoutStamp(e)) {
      return p.modifiedAt === e.modifiedAt ? e : { ...e, modifiedAt: p.modifiedAt };
    }
    return { ...e, modifiedAt: now };
  });
}

/**
 * Merge incoming entries into `local`. Returns the new list, or `null` when
 * nothing changes (so the caller can skip a write and a re-render).
 * Local order is kept; adopted new entries are appended.
 */
export function mergeLibraryEntries<T extends LibraryEntry>(local: readonly T[], incoming: readonly T[]): T[] | null {
  const best = new Map<string, T>();
  for (const e of incoming) {
    if (!e || typeof e.id !== 'string') continue;
    const cur = best.get(e.id);
    if (!cur || (e.modifiedAt ?? 0) >= (cur.modifiedAt ?? 0)) best.set(e.id, e);
  }
  if (best.size === 0) return null;
  let changed = false;
  const localIds = new Set(local.map((e) => e.id));
  const out = local.map((e) => {
    const inc = best.get(e.id);
    if (!inc) return e;
    if (JSON.stringify(inc) === JSON.stringify(e)) return e;
    if ((inc.modifiedAt ?? 0) < (e.modifiedAt ?? 0)) return e;
    changed = true;
    return inc;
  });
  for (const [id, e] of best) {
    if (localIds.has(id)) continue;
    out.push(e);
    changed = true;
  }
  return changed ? out : null;
}

/**
 * Every `sequencerId` and `macroId` referenced anywhere in a project's items,
 * in first-seen order. Walks the item objects generically rather than by kind,
 * because `macroId` also sits on a button's action config, not only on macro
 * items - a kind switch is how the export bundle came to miss that one.
 */
export function referencedLibraryIds(project: Project): { sequencerIds: string[]; macroIds: string[] } {
  const seq = new Set<string>();
  const mac = new Set<string>();
  const walk = (v: unknown, depth: number): void => {
    if (depth > 8 || v === null || typeof v !== 'object') return;
    if (Array.isArray(v)) { for (const x of v) walk(x, depth + 1); return; }
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      if (k === 'sequencerId' && typeof x === 'string' && x) seq.add(x);
      else if (k === 'macroId' && typeof x === 'string' && x) mac.add(x);
      else walk(x, depth + 1);
    }
  };
  for (const page of project.pages ?? []) walk(page.items, 0);
  return { sequencerIds: [...seq], macroIds: [...mac] };
}
